import React from 'react'
import { useSelector } from 'react-redux';
import { PortInfo, getStatusIcon, setObjectType } from '../../utils';
import { validateConnection } from '../../utils/DesignerUtils';
import NodeContent from './NodeContent';
import { Connection, Handle, NodeProps, Position } from 'reactflow';
import { WorkflowMode, WorkflowStatusModel } from '../../models/Workflow';
import { EventEmitter } from '../../utils/EventEmitter';
import { getUsedItemSelector } from '../../slices/designerSlice';
import { UsedItem } from '../../models/generics';
import { BdaDataset } from '../../models/BdaDataset';
import { BdaService } from '../../models/BdaService';
import classNames from 'classnames';

type ServiceNodeData = {
  inputs: PortInfo[];
  outputs: PortInfo[];
  mode: WorkflowMode;
  text: string;
  statusModel: WorkflowStatusModel;
  label: string;
  version: string;
  placeholder?: boolean;
}

export default function ServiceNode({ id, data, isConnectable }: NodeProps<ServiceNodeData>) {
  // const dispatch = useDispatch();
  const mode = data.mode.toLowerCase();
  const getUsedItem = useSelector(getUsedItemSelector);

  const isValidConnection = (connection: Connection): boolean => {
    const isValid = connection.source !== connection.target && validateConnection(connection.sourceHandle!, connection.targetHandle!);
    if (!isValid) return false;

    const sourceItem = getUsedItem(connection.source)!;
    if(!sourceItem) return true; // could be a placeholder dataset/model

    if (sourceItem.type === 'dataset' && setObjectType<UsedItem<BdaDataset>>(sourceItem)) { // targetItem can only be the current service
      const input = data.inputs.find(input => input.id === connection.targetHandle)!;
      return (sourceItem.datasource!.type === 'messageBroker' && input.datasetMode === 'streaming') || (sourceItem.datasource!.type !== 'messageBroker' && input.datasetMode !== 'streaming');
    }
    else if ((sourceItem.type === 'service') && setObjectType<UsedItem<BdaService>>(sourceItem)) { // TODO replace service type with just 'service' ???
      const targetItem = getUsedItem(connection.target) as UsedItem<BdaService>;
      const sourceOutput = (sourceItem.outputs as PortInfo[]).find(output => output.id === connection.sourceHandle);
      const targetInput = (targetItem.inputs as PortInfo[]).find(input => input.id === connection.targetHandle);
      return sourceOutput?.datasetMode === targetInput?.datasetMode;
    }

    return true;
  }
  const handlePortClick = (portId: string, generic?: boolean, description?: string) => (evt: React.MouseEvent<HTMLDivElement>) => {
    evt.stopPropagation();

    const portInfo: PortInfo = {
      id: portId,
      nodeId: id,
      nodeName: data.text,
      description,
      generic,
    };
    EventEmitter.dispatch('selected-port', portInfo);
  }

  return (
    <div className={classNames("designer-node service-node", { placeholder: data.placeholder })}>
      {data.statusModel && <div className="service-status">{getStatusIcon(data.statusModel.status)}</div>}

      <div className="input-ports">
        {data.inputs.map(({ id, type, datasetMode, generic, description }) => (
          <Handle
            className={classNames("custom-handle service-handle", type, datasetMode, { generic })}
            key={id}
            type="target"
            position={Position.Top}
            id={id}
            isConnectable={isConnectable}
            isValidConnection={isValidConnection}
            onClick={handlePortClick(id, generic, description)}
          />
        ))}
      </div>

      <NodeContent title={data.label} type={mode} version={data.version} vertical />

      <div className="output-ports">
        {data.outputs.map(({ id, type, datasetMode, generic, description }) => (
          <Handle
            type="source"
            key={id}
            className={classNames("custom-handle service-handle", type, datasetMode, { generic })}
            position={Position.Bottom}
            id={id}
            isConnectable={isConnectable}
            isValidConnection={isValidConnection}
            onClick={handlePortClick(id, generic, description)}
          />
        ))}
      </div>
    </div>
  );
}
